'use client';

import { type ReactNode, useId, useState } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import * as Popover from '@/app_components/ui/primitives/Popover';
import { cn, unwrapLazy } from '@/app_lib/utils/utils';

const tooltipStyles = tv({
  defaultVariants: {
    close: false,
    position: 'body',
  },
  slots: {
    base: 'appearance-none',
    close: '',
    contentWrapper: 'max-w-[240px]',
  },
  variants: {
    close: {
      false: {
        close: 'hidden',
      },
    },
    position: {
      body: {
        contentWrapper: 'z-body-popover',
      },
      header: {
        contentWrapper: 'z-header-popover',
      },
    },
  },
});

type TooltipVariants = VariantProps<typeof tooltipStyles>;

interface TooltipProps extends TooltipVariants {
  align?: 'start' | 'center' | 'end';
  className?: string;
  contentClassName?: string;
  children: ReactNode;
  content: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  title?: string;
}

export const Tooltip = ({
  className,
  children: trigger,
  title,
  content,
  contentClassName = 'z-body-popover',
  align = 'start',
  side = 'bottom',
  close,
  position,
}: TooltipProps) => {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  // TODO: While this is opened, other popovers should close
  // Do we want to use a context for this? Something to handle all event types?
  // Aka a context that handles modals, popovers, dropdowns, etc.

  return (
    <button
      aria-expanded={isOpen || isClicked}
      aria-haspopup='true'
      className={cn(tooltipStyles().base(), className)}
      id={`tooltip-trigger-${id}`}
      onClick={(e) => {
        setIsClicked((prev) => !prev);
        e.stopPropagation();
        e.preventDefault();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsClicked((prev) => !prev);
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      onMouseLeave={() => setIsOpen(false)}
      type='button'
    >
      <Popover.Root
        modal={false}
        onOpenChange={setIsClicked}
        open={isOpen || isClicked}
      >
        <Popover.Trigger
          asChild
          className='cursor-pointer'
          onMouseEnter={() => setIsOpen(true)}
          tabIndex={-1} // Prevents double focus on trigger
        >
          {unwrapLazy(trigger)}
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align={align}
            avoidCollisions={true}
            className={cn(contentClassName, tooltipStyles().contentWrapper({ position }))}
            collisionPadding={8}
            onClick={(e) => e.stopPropagation()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            side={side}
          >
            <div className='border-t-4 stroke-accent bg-pure rounded-sm p-xxs mt-1 shadow-sm'>
              <div className='flex justify-between items-center gap-sm typography-sm'>
                <p className='font-semibold'>{title}</p>
                <Popover.Close
                  asChild
                  className={cn(tooltipStyles().close({ close }))}
                  onClick={() => {
                    setIsClicked(false);
                    setIsOpen(false);
                  }}
                >
                  <ActionableIcon>
                    <Icon icon='X' />
                  </ActionableIcon>
                </Popover.Close>
              </div>
              <p>{content}</p>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </button>
  );
};
